export const TONES = ["jue", "zhi", "gong", "shang", "yu"] as const;
export const MOODS = ["relax", "focus", "meditate", "sleep", "renew"] as const;
export const INSTRUMENTS = ["guqin", "guzheng", "xiao", "bamboo-flute", "mixed"] as const;
export const DURATIONS = [30, 60, 120, 240] as const;

export type Tone = (typeof TONES)[number];
export type Mood = (typeof MOODS)[number];
export type Instrument = (typeof INSTRUMENTS)[number];
export type Duration = (typeof DURATIONS)[number];
export type GenerationMode = "instrumental" | "song";
export type LyricsInputType = "idea" | "lyrics";

export type GenerationStatus =
  | "PENDING"
  | "LEASED"
  | "SUBMITTING"
  | "SUBMITTED"
  | "RUNNING"
  | "GENERATED"
  | "READY_TEMPORARY"
  | "ARCHIVING"
  | "READY_ARCHIVED"
  | "SUBMISSION_UNKNOWN"
  | "FAILED"
  | "CANCELLED"
  | "EXPIRED";

export interface Plan {
  id: "free-preview" | "personal-pack" | "deep-practice-pack";
  name: string;
  priceUsd: number;
  credits: number;
  creditValidityDays: number;
  archiveDays: 0 | 30 | 90;
  purchasable: boolean;
}

export interface CreateGenerationRequest {
  mode: GenerationMode;
  tone: Tone;
  mood: Mood;
  instrument: Instrument;
  duration: Duration;
  lyricsDraftId?: string;
  title?: string;
  lyrics?: string;
  prompt?: string;
  vocalStyle?: string;
}

export interface GenerationView extends CreateGenerationRequest {
  id: string;
  status: GenerationStatus;
  creditsReserved: number;
  createdAt: string;
  completedAt: string | null;
  expiresAt: string | null;
  errorCode: string | null;
  playUrl: string | null;
  downloadUrl: string | null;
  aiGenerated: true;
}

export type LyricsDraftStatus = "PENDING" | "LEASED" | "READY" | "FAILED" | "EXPIRED";

export interface CreateLyricsDraftRequest {
  inputType: LyricsInputType;
  content: string;
  duration: Duration;
}

export interface LyricsDraftView {
  id: string;
  status: LyricsDraftStatus;
  inputType: LyricsInputType;
  duration: Duration;
  title: string | null;
  lyrics: string | null;
  prompt: string | null;
  tone: Tone | null;
  mood: Mood | null;
  instrument: Instrument | null;
  vocalStyle: string | null;
  model: string | null;
  errorCode: string | null;
  createdAt: string;
  expiresAt: string;
}

export interface CurrentUser {
  id: string;
  email: string;
  displayName: string;
  avatarUrl: string | null;
  creditBalance: number;
}

export type AgentOperation = "CREATE" | "POLL" | "ARCHIVE" | "LYRICS";

export interface AgentLease {
  jobId: string;
  operation: AgentOperation;
  leaseToken: string;
  leaseExpiresAt: string;
  payload: {
    prompt?: string;
    lyrics?: string;
    duration?: Duration;
    providerTaskId?: string;
    providerTaskUrl?: string;
    providerFileUrl?: string;
    lyricsInputType?: LyricsInputType;
    lyricsInput?: string;
  };
}

export const TONE_LABELS: Record<Tone, { title: string; element: string; description: string }> = {
  jue: { title: "Jue", element: "Wood", description: "Renewing, open and gently uplifting." },
  zhi: { title: "Zhi", element: "Fire", description: "Warm, luminous and softly energizing." },
  gong: { title: "Gong", element: "Earth", description: "Grounded, balanced and reassuring." },
  shang: { title: "Shang", element: "Metal", description: "Clear, reflective and spacious." },
  yu: { title: "Yu", element: "Water", description: "Deep, quiet and sleep-oriented." }
};

export function isOneOf<T extends readonly unknown[]>(values: T, value: unknown): value is T[number] {
  return values.includes(value as never);
}

export function creditsForDuration(duration: Duration): number {
  if (duration <= 60) return 1;
  if (duration === 120) return 2;
  return 4;
}
